import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { timeoutDuration } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import {
  getHeader,
  getHeaderHeight,
  makeHeaderFixed,
  removeFixedHeader
} from 'utils/helpers/fixed-header';
import { isMobile } from 'utils/is-mobile';
import { ICasinoCachedGames } from 'interfaces/casino-data';
import { setHeaderState } from 'store/actions/app-data';
import { getHeaderState } from 'store/selectors/app-data';
import { getBetslipOpen, getQuickBetEnabled } from 'store/selectors/betslip';
import { getActiveGames } from 'store/selectors/new-casino';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

export const useHeaderVisibility = (): void => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isQuickBetEnabled = useSelector(getQuickBetEnabled);
  const isBetslipOpened = useSelector(getBetslipOpen);
  const activeGames = useSelector(getActiveGames) as ICasinoCachedGames;
  const { isVisible, height: headerHeight } = useSelector(getHeaderState);

  const isHeaderVisibleForCasino =
    SpringConfigs.IS_HEADER_VISIBLE_IN_CASINO &&
    isLoggedIn &&
    Object.keys(activeGames).length > 0;

  const isHeaderVisibleForBetslip = useMemo(
    () => isMobile() && isLoggedIn && isBetslipOpened && !isQuickBetEnabled,
    [isLoggedIn, isBetslipOpened, isQuickBetEnabled]
  );

  const removeHeader = () => {
    if (getHeader()) {
      removeFixedHeader();

      setTimeout(() => {
        dispatch(setHeaderState({ isVisible: false, height: 0 }));
      }, timeoutDuration.DURATION_100);
    }
  };

  useLayoutEffect(() => {
    if (getHeader()) {
      if (
        (isHeaderVisibleForCasino || isHeaderVisibleForBetslip) &&
        !isVisible
      ) {
        makeHeaderFixed();

        const updateHeaderHeight = () => {
          const height = getHeaderHeight();

          if (headerHeight !== height) {
            dispatch(setHeaderState({ height }));
          }
        };

        updateHeaderHeight();
        getHeader().addEventListener('resize', updateHeaderHeight);

        dispatch(setHeaderState({ isVisible: true }));
      }

      if (!isHeaderVisibleForCasino && !isHeaderVisibleForBetslip) {
        removeHeader();
      }
    }
  }, [user, isHeaderVisibleForBetslip, isHeaderVisibleForCasino]);

  useEffect(() => {
    return removeHeader;
  }, []);
};
